import React from 'react';
import { Modal, Spin } from 'antd';
import './oneButton-css/ProductionsCard.css';
import './oneButton-css/DashboardModal.css';

type ForceSyncConfirmContentParams = {
    systemDesign: any;
    isBatteryInfoUpdated?: boolean;
    isEquipmentModified: boolean;
};

/** Same conditional copy as `ProductionsCard` force-sync confirm modal. */
export function buildForceSyncConfirmContent(params: ForceSyncConfirmContentParams): React.ReactNode {
    const { systemDesign, isBatteryInfoUpdated = false, isEquipmentModified } = params;

    if (systemDesign?.IsOutOfTolerance) {
        return (
            <p>
                Either the System Size or Annual Production Estimate have been updated and fall outside the Lightreach
                tolerance threshold. This change will require a customer re-sign, and the current active contract and
                quote will be voided. You will need to create a new quote for submission.
            </p>
        );
    }
    if (systemDesign?.IsWithinTolerance) {
        return (
            <div>
                <b>Notice</b>: The System Size or Annual Production Estimate has been updated, but the change remains
                within Lightreach&apos;s tolerance threshold.No customer re-sign is required.
                <ul>
                    <li> - To sync the design changes with Lightreach, click <b>OK</b>.</li>
                    <li> - To save the design without syncing, click <b>Cancel</b>.</li>
                </ul>
                <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design,
                save it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote.
                This ensures the quote reflects the correct system size and accurate financial details.
            </div>
        );
    }
    if (isBatteryInfoUpdated) {
        return (
            <div>
                <div>
                    <b>Notice</b>: Battery Qty has been changed. This will require a customer re-sign.
                </div>
                <ul>
                    <li> - To sync the Design / Battery Qty changes with Lightreach, click <b>OK</b>.</li>
                    <li> - To save the Design / Battery Qty changes without syncing, click <b>Cancel</b>.</li>
                </ul>
                <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design,
                save it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote.
                This ensures the quote reflects the correct system size and accurate financial details.
            </div>
        );
    }
    if (isEquipmentModified) {
        return (
            <div>
                You have updated the equipment for this design. A new change order will be created and can be submitted
                to Lightreach by clicking the ‘Submit Change Order’ button on the active quote in the Agreement page. This
                change does not require a customer re-sign.
            </div>
        );
    }
    return (
        <div>
            <b>Notice</b>: System design has changed since the last update to Light reach, but these design changes have
            not yet been synced.
            <ul>
                <li> To sync the design changes with Lightreach, click <b>OK</b>.</li>
                <li> To save the design without syncing, click <b>Cancel</b>.</li>
            </ul>
            <b>Important</b>: If you click Cancel but plan to create a new quote, you must return to this design, save
            it, and sync it with the Lightreach portal by Clicking <b>“OK” before</b> generating the quote. This ensures
            the quote reflects the correct system size and accurate financial details.
        </div>
    );
}

export type SystemDesignForceSyncConfirmModalProps = {
    open: boolean;
    loading?: boolean;
    theme?: 'dark' | 'light';
    systemDesign: any;
    isBatteryInfoUpdated?: boolean;
    isEquipmentModified: boolean;
    /** Default 13200 (dashboard). Use 1000001+ when stacking above viewer modals. */
    zIndex?: number;
    getContainer?: false | HTMLElement | (() => HTMLElement);
    onOk: () => void;
    onCancel: () => void;
    afterClose?: () => void;
};

/**
 * Shared force-sync confirm UI — same layout as `ProductionsCard` mergeWithSystem refresh modal.
 */
export const SystemDesignForceSyncConfirmModal: React.FC<SystemDesignForceSyncConfirmModalProps> = ({
    open,
    loading = false,
    theme = 'light',
    systemDesign,
    isBatteryInfoUpdated = false,
    isEquipmentModified,
    zIndex = 13200,
    getContainer,
    onOk,
    onCancel,
    afterClose,
}) => (
    <Modal
        title="Confirm system design refresh"
        open={open}
        zIndex={zIndex}
        centered
        maskClosable={!loading}
        closable={!loading}
        okText="Continue"
        cancelText="Cancel"
        cancelButtonProps={isEquipmentModified ? { style: { display: 'none' } } : undefined}
        rootClassName={`obd-theme-${theme} obd-productions-force-sync-confirm`}
        getContainer={getContainer}
        modalRender={(modal) => (
            <div className="obd-productions-force-sync-confirm__shell">
                {modal}
                {loading ? (
                    <div
                        className="obd-dashboard-modal__loading-overlay"
                        role="status"
                        aria-live="polite"
                        aria-busy="true"
                    >
                        <div className="obd-dashboard-modal__loading-inner">
                            <Spin size="default" />
                            <span className="obd-dashboard-modal__loading-text">Processing…</span>
                        </div>
                    </div>
                ) : null}
            </div>
        )}
        onOk={onOk}
        onCancel={onCancel}
        afterClose={afterClose}
    >
        {buildForceSyncConfirmContent({
            systemDesign,
            isBatteryInfoUpdated,
            isEquipmentModified,
        })}
    </Modal>
);

export default SystemDesignForceSyncConfirmModal;
